/* eslint-disable max-len */
import React, {useEffect, useState} from "react";
import {data as chartData} from "./chartData";
import {withResizeDetector} from "react-resize-detector";

import {v4} from "uuid";
import {AUTO_REFRESH_OPTIONS} from "../widget-constants";
import {EQ_PARAMS} from "./constants";

import DeviceInventoryForm from "./DeviceInventoryForm";
import ModalDefault from "../../../common/modal/modal-default";
import Loader from "../../../common/loader/Loader";
import WidgetEmpty from "../empty-widget-content/empty-widget-content";
import SunburstChart from "../../../common/charts/sunburst/SunburstChart";

import {goToSolutions} from "../widget-helpers";
import {
    reduceColumnConfig,
    getInitialColumnsConfig,
    getFilterColumnsConfig,
    getSingleLabelRedirectFilter,
} from "./device-inventory-helper";

import {deviceManagerDevicesColumnConfig} from "../../../../pagingfeed";
import DashboardService from "../../../../services/dashboard-service";

import "./device-inventory.scss";
import WidgetHeader from "../widget-header/WidgetHeader";
import WidgetHeaderButton from "../widget-header/widget-header-button/WidgetHeaderButton";

const INITIAL_VALUES = {
    title: "",
    filter: {
        mongoQuery: {},
    },
    filterName: "",
    autoRefresh: AUTO_REFRESH_OPTIONS[0],
    columnsConfig: {},
    selectedFields: null,
    selectedPills: null,
};

const DeviceInventoryWidget = ({
    showWidgetInfo,
    onRemove,
    onClone,
    onUpdate,
    isEditAllowed,
    height,
    width,
    data,
    isMobileDevice,
    openSolutionsInNewTab,
}) => {
    const [formData, setFormData] = useState({
        title: data.title || INITIAL_VALUES.title,
        filter: data.config.filter || INITIAL_VALUES.filter,
        filterName: data.config.filterName || INITIAL_VALUES.filterName,
        autoRefresh: data.config.autoRefresh || INITIAL_VALUES.autoRefresh,
        selectedFields: data.config.selectedFields || INITIAL_VALUES.selectedFields,
        selectedPills: data.config.selectedPills || INITIAL_VALUES.selectedPills,
    });
    const [tempConfigData, setTempConfigData] = useState({
        dataType: {
            label: "Devices",
            value: "DEVICES",
        },
        filter: data.config.filter || INITIAL_VALUES.filter,
        columnsConfig: data.config.columnsConfig || INITIAL_VALUES.columnsConfig,
        selectedFields: data.config.selectedFields || INITIAL_VALUES.selectedFields,
        selectedPills: data.config.selectedPills || INITIAL_VALUES.selectedPills,
    });
    const [isSettingsModalShown, setIsSettingsModalShown] = useState(false);
    const [columnsConfig, setColumnsConfig] = useState({});
    const [filterColumnsConfig, setFilterColumnsConfig] = useState({});
    const [dataInventoryItems, setDataInventoryItems] = useState(null);
    const [echartData, setEchartData] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);

    const fetchDeviceInventoryItems = async (filter, fields) => {
        setIsLoadingData(true);
        const inventoryItems = await DashboardService.getDeviceInventoryItems(filter, fields);
        setDataInventoryItems(inventoryItems);
        setEchartData(
            inventoryItems[0] && inventoryItems[0].children ? inventoryItems[0].children : []
        );
        setIsLoadingData(false);
    };

    const refreshData = () => {
        const filter = formData.filter;
        const fields = formData.selectedFields;

        fetchDeviceInventoryItems(filter, fields);
    };

    useEffect(() => {
        const loadColumnConfig = async () => {
            const dataColumnsConfig = data.config.columnsConfig;
            const initialColumnsConfig = await deviceManagerDevicesColumnConfig.getColumnConfig();
            const columnsConfig = await getInitialColumnsConfig(
                dataColumnsConfig,
                initialColumnsConfig
            );
            const filterColumnsConfigInit = getFilterColumnsConfig(initialColumnsConfig);

            setColumnsConfig(columnsConfig);
            setFilterColumnsConfig(filterColumnsConfigInit);
            setTempConfigData((value) => ({
                ...value,
                columnsConfig,
                filterColumnsConfig: filterColumnsConfigInit,
            }));
        };

        loadColumnConfig();
    }, [data.config.columnsConfig]);

    useEffect(() => {
        const tryFetch = () => {
            if (!formData.selectedFields) {
                return;
            }
            fetchDeviceInventoryItems(formData.filter, formData.selectedFields);
        };

        tryFetch();

        const fetchInterval = setInterval(tryFetch, formData.autoRefresh.value);

        return () => {
            clearInterval(fetchInterval);
        };
    }, [formData]);

    const widgetId = data.id || v4();

    const handleChartRedirect = () => {
        const openSolutionInNewTab = openSolutionsInNewTab;

        if (isMobileDevice || isEditAllowed) {
            return;
        }

        let obj = {
            filter: formData.filter.mongoQuery,
            groupingField: {},
        };

        if (formData.filter.mongoQuery) {
            obj = {
                ...obj,
                filter: formData.filter.mongoQuery,
            };
        }

        goToSolutions(
            obj,
            tempConfigData.dataType.value,
            isMobileDevice,
            undefined,
            openSolutionInNewTab
        );
    };

    const handleSingleLabelRedirect = (params) => {
        if (isMobileDevice || isEditAllowed) {
            return;
        }

        const openSolutionInNewTab = openSolutionsInNewTab;
        const childrenLength = params.data.children.length;

        if (childrenLength !== 0) {
            return;
        }
        const selectedFields = formData.selectedFields;

        const clickedFilter = getSingleLabelRedirectFilter(
            params.data,
            dataInventoryItems,
            selectedFields,
            EQ_PARAMS,
            columnsConfig
        );

        // Merge with base widget filter — flatten $and conditions to avoid double-nesting
        const baseFilter = formData.filter?.mongoQuery;
        let filter;
        if (baseFilter) {
            const conditions = clickedFilter.$and || [clickedFilter];
            filter = {$and: [...conditions, baseFilter]};
        } else {
            filter = clickedFilter;
        }

        const obj = {
            filter,
            groupingField: {},
        };
        goToSolutions(
            obj,
            tempConfigData.dataType.value,
            isMobileDevice,
            undefined,
            openSolutionInNewTab
        );
    };

    const toggleSettingsModal = (e) => {
        e.stopPropagation();
        setTempConfigData({
            ...tempConfigData,
            filter: {...formData.filter},
            selectedFields: formData.selectedFields,
            selectedPills: formData.selectedPills,
            columnsConfig,
            filterColumnsConfig,
        });
        setIsSettingsModalShown(!isSettingsModalShown);
    };

    const handleFormFieldChange = (field, props) => (value) => {
        setTempConfigData(() => ({
            ...tempConfigData,
            [field]: value,
        }));

        props.setFieldValue(field, value);
    };

    const saveColumnConfig = (columnsConfig) => {
        setTempConfigData((value) => ({
            ...value,
            columnsConfig,
        }));
    };

    const saveSelectedFields = (field, props) => (value) => {
        const fields = value.map((item) => item.name);
        const pills = value.map((item) => item.label);

        setTempConfigData(() => ({
            ...tempConfigData,
            [field]: fields,
            selectedPills: pills,
        }));

        props.setFieldValue(field, fields);
        props.setFieldValue("selectedPills", pills);
    };

    const handleSave = async (values) => {
        const updatedColumnConfig = await deviceManagerDevicesColumnConfig.getColumnConfig();
        Object.assign(INITIAL_VALUES, {columnsConfig: updatedColumnConfig});
        setFormData(values);
        setColumnsConfig(tempConfigData.columnsConfig);
        onUpdate({
            ...data,
            title: values.title,
            config: {
                ...data.config,
                autoRefresh: values.autoRefresh,
                filter: values.filter,
                filterName: values.filterName,
                widgetId,
                columnsConfig: reduceColumnConfig(tempConfigData.columnsConfig),
                selectedFields: tempConfigData.selectedFields,
                selectedPills: tempConfigData.selectedPills,
            },
        });
        setIsSettingsModalShown(false);
    };

    const renderContent = () => {
        if (!formData.selectedFields) {
            return (
                <WidgetEmpty
                    isEditAllowed={isEditAllowed}
                    showSettingsModal={toggleSettingsModal}
                    text="The initial widget configuration is empty."
                    dataTestId="glu-device-inventory-no-configuration"
                />
            );
        }

        if (
            formData.selectedFields.length &&
            dataInventoryItems &&
            dataInventoryItems.length === 1
        ) {
            return (
                <WidgetEmpty
                    isEditAllowed={isEditAllowed}
                    showSettingsModal={toggleSettingsModal}
                    text="No items match the specified filter criteria."
                    dataTestId="glu-device-inventory-no-filtered-data"
                />
            );
        }

        return (
            <div className="glu-content__section glu-content__separator glu-content__separator--inner glu-spread-v">
                <div className="glu-device-inventory">
                    <div
                        style={{
                            fontSize: `${height / 28}px`,
                        }}
                        className="glu-device-inventory-widget__filter-name"
                    >
                        {formData.filterName.length > 0 &&
                            formData.filter.mongoQuery &&
                            formData.filterName}
                    </div>
                    <SunburstChart
                        width={width}
                        options={chartData}
                        data={echartData}
                        handleChartClick={handleSingleLabelRedirect}
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="glu-dashboard-card glu-content glu-display-widget">
            <WidgetHeader
                isEditAllowed={isEditAllowed}
                title={data.title}
                height={height}
                width={width}
                showWidgetInfo={showWidgetInfo(data)}
                openConfiguration={toggleSettingsModal}
                onRemove={onRemove(data)}
                onClone={onClone(data)}
            >
                <WidgetHeaderButton
                    showButton={!isEditAllowed}
                    iconName="refresh"
                    onClick={refreshData}
                    hoverTooltip={"Refresh"}
                    customClassName={"glu-flex-shrink--large-padding"}
                />
                <WidgetHeaderButton
                    showButton={!isEditAllowed && !isMobileDevice}
                    iconName="exit-from-the-app"
                    onClick={handleChartRedirect}
                    hoverTooltip={"Open in Device Manager"}
                    customClassName={"glu-flex-shrink--large-padding"}
                />
            </WidgetHeader>
            {isLoadingData ? <Loader /> : renderContent()}

            {isSettingsModalShown && (
                <ModalDefault isOpen={isSettingsModalShown}>
                    <div className="glu-modal-content">
                        <div className="glu-modal__header">
                            <div className="glu-modal__title glu-flex-grow">Device Inventory</div>
                            <div className="glu-flex-shrink">
                                <button
                                    aria-label="close"
                                    type="button"
                                    className="glu-btn-link g-icon g-icon-close"
                                    onClick={toggleSettingsModal}
                                />
                            </div>
                        </div>
                        <DeviceInventoryForm
                            initialValues={formData}
                            onFormSubmit={handleSave}
                            onCancel={toggleSettingsModal}
                            configData={tempConfigData}
                            saveColumnConfig={saveColumnConfig}
                            handleFormFieldChange={handleFormFieldChange}
                            saveSelectedFields={saveSelectedFields}
                        />
                    </div>
                </ModalDefault>
            )}
        </div>
    );
};

export default withResizeDetector(DeviceInventoryWidget);
